import { useMemo } from "react";
import { useTeamStats } from "../../hooks/useTeamStats";
import type { Nurse, TeamNurse } from "../../utils/models";

/**
 * Staff who have never opened the app.
 *
 * This replaced a "Weekly active" card that read 0 on facilities with 130 active staff, which
 * looked like a broken metric and was really a true one: `lastLoginTime` comes from the joined
 * `user` row, so every nurse without an app account reads as never-logged-in and can never read
 * as anything else. Counting the quiet end states that plainly — and it is the question managers
 * actually ask of this column ("who has gone quiet"), rather than its inverse.
 */
export const getNeverLoggedInNurseIds = (nurses: Pick<Nurse, "id" | "lastLoginTime">[]): Set<number> =>
	new Set(nurses.filter((n) => !n.lastLoginTime).map((n) => n.id));

export type TeamScheduleStats = {
	workedLastMonthNurseIds: Set<number>;
	recurringShiftsNurseIds: Set<number>;
	/** Null when the department has no self-scheduling period: the card is hidden. */
	selfSchedulingSubmittedNurseIds: Set<number> | null;
};

export type TeamCardStats = {
	totalStaff: number;
	neverLoggedInNurseIds: Set<number>;
	/** Undefined until the server answers, and after it fails. */
	schedule: TeamScheduleStats | undefined;
	isSchedulePending: boolean;
	isScheduleError: boolean;
};

/**
 * The figures behind the Team page's stat cards. Roster-based ones are counted here; the schedule-based
 * ones come from the server, narrowed to the roster so a card never counts someone the table can't show.
 */
export const useTeamCardStats = (departmentId: string, roster: TeamNurse[]): TeamCardStats => {
	const { stats, isLoading, isError } = useTeamStats(departmentId);

	const schedule = useMemo<TeamScheduleStats | undefined>(() => {
		// A failed refetch keeps the last answer in `stats`; dropping it keeps a hidden tile from still filtering rows.
		if (!stats || isError) return undefined;
		const rosterIds = new Set(roster.map((n) => n.id));
		const onRoster = (ids: number[]) => new Set(ids.filter((id) => rosterIds.has(id)));
		return {
			workedLastMonthNurseIds: onRoster(stats.workedLastMonthNurseIds),
			recurringShiftsNurseIds: onRoster(stats.recurringShiftsNurseIds),
			selfSchedulingSubmittedNurseIds: stats.selfSchedulingSubmittedNurseIds === null ? null : onRoster(stats.selfSchedulingSubmittedNurseIds),
		};
	}, [stats, isError, roster]);

	const neverLoggedInNurseIds = useMemo(() => getNeverLoggedInNurseIds(roster), [roster]);

	return { totalStaff: roster.length, neverLoggedInNurseIds, schedule, isSchedulePending: isLoading, isScheduleError: isError };
};
